import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { cn } from '../utils/cn';
import { Label } from './Label';
import { Check, ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

export interface SelectProps {
  label?: string;
  error?: string;
  hint?: string;
  options: SelectOption[];
  optionFormat?: 'label' | 'full';
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  className?: string;
  id?: string;
  disabled?: boolean;
}

export function Select({
  className,
  label,
  error,
  hint,
  id,
  options,
  optionFormat = 'full',
  value: controlledValue,
  defaultValue = '',
  onChange,
  disabled = false,
}: SelectProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const selectedValue = controlledValue ?? internalValue;

  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex] as HTMLElement;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, isOpen]);

  const selectedOption = options.find((o) => o.value === selectedValue);

  function formatOption(option: SelectOption): string {
    return optionFormat === 'full' && option.description ? `${option.label} – ${option.description}` : option.label;
  }

  function handleSelect(optionValue: string) {
    setInternalValue(optionValue);
    setIsOpen(false);
    onChange?.(optionValue);
  }

  function handleKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setHighlightedIndex(0);
        } else {
          setHighlightedIndex((i) => Math.min(i + 1, options.length - 1));
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex((i) => Math.max(i - 1, 0));
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          handleSelect(options[highlightedIndex]?.value ?? '');
        } else {
          setIsOpen(true);
          setHighlightedIndex(
            Math.max(
              options.findIndex((o) => o.value === selectedValue),
              0,
            ),
          );
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
    }
  }

  return (
    <div className="space-y-1.5" ref={containerRef}>
      {label && <Label htmlFor={selectId}>{label}</Label>}
      <div className="relative">
        <button
          type="button"
          id={selectId}
          disabled={disabled}
          className={cn(
            'w-full text-left rounded-lg border px-3 py-2 pr-10 text-sm',
            'bg-transparent text-text-primary',
            'transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
            error
              ? 'border-feedback-error focus:ring-feedback-error'
              : 'border-border-strong hover:border-border-input-hover',
            disabled && 'opacity-50 cursor-not-allowed',
            className,
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          {selectedOption ? formatOption(selectedOption) : <span className="text-text-muted">Select…</span>}
        </button>
        <ChevronDown
          className={cn(
            'pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
          aria-hidden="true"
        />
        {isOpen && (
          <ul
            ref={listRef}
            role="listbox"
            className="absolute z-50 mt-1 w-full rounded-lg border border-border-strong bg-bg-elevated shadow-lg py-1"
          >
            {options.map((option, index) => {
              const isSelected = option.value === selectedValue;
              const isHighlighted = index === highlightedIndex;
              return (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={isSelected}
                  className={cn(
                    'px-3 py-2 text-sm cursor-pointer transition-colors',
                    'text-text-primary',
                    isHighlighted && 'bg-bg-subtle',
                    isSelected && !isHighlighted && 'bg-primary-500/10',
                    !isHighlighted && !isSelected && 'hover:bg-bg-subtle',
                  )}
                  onClick={() => handleSelect(option.value)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="flex items-center justify-between">
                    <span>{formatOption(option)}</span>
                    {isSelected && <Check className="h-4 w-4 text-primary-500 shrink-0 ml-2" />}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {error && <p className="text-xs text-feedback-error">{error}</p>}
      {hint && !error && <p className="text-xs text-text-muted">{hint}</p>}
    </div>
  );
}

Select.displayName = 'Select';
